/**
 * Entities the SERVER refused to accept, kept until the user resolves them.
 *
 * The bug this closes: the free plan caps collections per workspace and
 * requests per collection. Crossing a cap writes the row locally, queues it,
 * and the server rejects the push with a human-readable plan message — which
 * the Rust engine used to hand to `log::warn!` and nothing else. The outbox row
 * is then dropped (keeping it would poison-pill the per-entity FIFO queue), the
 * local row keeps the user's edit, and no pull ever redelivers it. The result:
 * the user believes the work is synced, and discovers otherwise on a second
 * machine, where it is indistinguishable from data loss.
 *
 * A notification alone is not enough — it is dismissible and the user may be
 * away from the keyboard when it fires — so the fact is ALSO parked here,
 * keyed by entity id, so any surface (sidebar row, collection header, sync
 * indicator) can mark the affected item as local-only for as long as it is.
 *
 * Deliberately NOT in notificationStore: that store auto-dismisses and is a
 * transient feed. This is durable state about specific rows.
 */
import { create } from 'zustand';
import { addSessionEndedListener } from '../services/channelService';

/** Normalizes one wire entry from the `sync:rejected` payload, or null. */
function toEntry(workspaceId, raw) {
  const entityId = raw?.entityId;
  if (!entityId) return null;
  return {
    entityId: String(entityId),
    entityType: String(raw?.entityType || ''),
    workspaceId: String(workspaceId || ''),
    // The server's own sentence, verbatim. Never reworded here: only the
    // server knows which limit was hit and what the plan is called, and any
    // client-side copy goes stale the next time quotas or pricing move.
    error: String(raw?.error || ''),
    at: Date.now(),
  };
}

export const useSyncRejectionStore = create((set, get) => ({
  /** entityId -> { entityId, entityType, workspaceId, error, at } */
  byEntityId: {},

  /**
   * Feeds a raw `sync:rejected` payload
   * ({ workspaceId, rejected: [{ entityId, entityType, error }] }).
   *
   * Returns ONLY the entries that are news — a first rejection for that
   * entity, or the same entity rejected for a different reason. Re-editing a
   * row that is over quota re-queues and re-rejects it on every engine cycle;
   * without this the user would get an identical notification every 30
   * seconds and would learn to ignore the one signal that matters. The marker
   * itself is refreshed either way (its `at` moves), so "still local-only"
   * stays true even when nothing new is announced.
   */
  record: (payload) => {
    const entries = (Array.isArray(payload?.rejected) ? payload.rejected : [])
      .map((raw) => toEntry(payload?.workspaceId, raw))
      .filter(Boolean);
    if (entries.length === 0) return [];

    const existing = get().byEntityId;
    const fresh = entries.filter((e) => existing[e.entityId]?.error !== e.error);

    set((s) => {
      const next = { ...s.byEntityId };
      entries.forEach((e) => {
        next[e.entityId] = e;
      });
      return { byEntityId: next };
    });
    return fresh;
  },

  /**
   * Clears the marker for one entity. Called when the row finally syncs (or
   * the user deletes it) — nothing else clears it, because nothing else
   * proves the server accepted the row.
   */
  clearEntity: (entityId) => {
    if (!entityId || !(entityId in get().byEntityId)) return;
    set((s) => {
      const next = { ...s.byEntityId };
      delete next[String(entityId)];
      return { byEntityId: next };
    });
  },

  /** Logout / account switch: a departed session's rejections are not the next account's. */
  reset: () => set({ byEntityId: {} }),
}));

// Same self-reset contract as syncStatusStore: subscribing here means the
// store cleans itself on EVERY session end (hard logout and the soft 401
// interceptor path alike) instead of depending on one logout function
// remembering to call it.
addSessionEndedListener(() => {
  useSyncRejectionStore.getState().reset();
});
